import { useEffect, useState, useRef } from 'react';
import { View, Pressable, Platform, Animated, StyleSheet } from 'react-native';
import { Text } from 'tamagui';
import * as WebBrowser from "expo-web-browser";
import * as Google from "expo-auth-session/providers/google";
import { HIDE_LOADING_MODAL, SHOW_AUTH_MODAL, SHOW_LOADING_MODAL, SHOW_OTP, SHOW_TC_MODAL, SUCCESS } from '@reduxLocal/actions/types';
import { DefaultRootState } from '@reduxLocal/persistState';
import { createURL } from 'expo-linking';
import { GoogleIcon } from '@assets/--native';
import apis from '@api/api';
import { useDispatch, useSelector } from 'react-redux';
import React from 'react';
import DeviceInfo from 'react-native-device-info';
import styles from '@stylesheet';
import { useNavigation } from '@react-navigation/native';
import { UserType } from './types/UserType';
import { LinearGradient } from 'expo-linear-gradient';
import {
  handleError,
  handleOAuthResponse,
  createOAuthUser,
  fetchUserInfoFromProvider,
  OAuthUser
} from './utils/authUtils';
import { getAuthSessionGuard } from '@utils/authSessionGuard';

WebBrowser.maybeCompleteAuthSession();

// Get auth session guard singleton
const authGuard = getAuthSessionGuard();


const GoogleButton = ({ code, productId }: {
  code?: string;
  productId?: string;
}) => {
  const dispatch = useDispatch();
  //Google Authentication Vars
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const [token, setToken] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  const navigation = useNavigation();
  const notificationToken: string = useSelector(
    (state: DefaultRootState) => state.app.notificationToken
  );

  const [request, response, promptAsync] = Google.useAuthRequest({
    androidClientId: "530616870665-mt8uulmtk3r41m5ga22sfuc66j632gik.apps.googleusercontent.com",
    iosClientId: "530616870665-sn6qm81jg6eh4pr6dm2ov2n6guovrgna.apps.googleusercontent.com",
    expoClientId: '530616870665-5e22kgtr0e146vutn8veh5shoqnkis5j.apps.googleusercontent.com',
    webClientId: '530616870665-5e22kgtr0e146vutn8veh5shoqnkis5j.apps.googleusercontent.com',
    redirectUri: createURL(''),
    scopes: ['email', 'profile']
  });

  useEffect(() => {
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 1000,
      useNativeDriver: true,
    }).start();
  }, []);

  const handleClick = async () => {
    // Check if another auth session is active
    if (authGuard.isSessionActive()) {
      const activeSession = authGuard.getActiveSession();
      console.warn(`Cannot start Google auth - ${activeSession} is already active`);
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: {
          content: `Please wait for ${activeSession} authentication to complete`,
          type: 'error'
        }
      });
      return;
    }

    // Start auth session with guard
    const res = await authGuard.startSession('Google', async () => {
      return await promptAsync();
    });

    if (res.type === 'cancel') {
      //Timeout addresses the presented sheet
      setTimeout(() => handleError(dispatch, 'Login was cancelled'), 1000);
    } else if (res.type === 'error') {
      console.error('Google auth error:', res.error);
    }

    return res;
  }

  useEffect(() => {
    if (response?.type === "success") {
      setToken(response.authentication.accessToken);
      getUserInfo(response.authentication.accessToken)
    }
  }, [response]);

  async function getUserInfo(token) {
    if (response?.type != 'success') {
      handleError(dispatch, 'Failed to authenticate Google Account, please try again');
    }
    if (!token) {
      return
    }
    try {
      dispatch({ type: SHOW_LOADING_MODAL });

      // Fetch user info from Google API
      const user = await fetchUserInfoFromProvider(
        "https://www.googleapis.com/userinfo/v2/me",
        token,
        setUserInfo,
        'Google'
      );

      const newOAuthUser: OAuthUser = createOAuthUser(user, token, 'google', notificationToken);

      // Handle OAuth flow using shared utility
      const res = await apis.handleOAuth(newOAuthUser);
      await handleOAuthResponse(res, dispatch, navigation, notificationToken, 'Google');

    } catch (e) {
      handleError(dispatch, 'Google login failed, please try again. If the issue persists, contact support.');
    } finally {
      dispatch({ type: HIDE_LOADING_MODAL });
    }
  };

  return (
    <Pressable
      onPress={handleClick}
      style={{
        minHeight: Platform.select({ web: 50, default: 50 }), height: 50, maxHeight: 50, flex: 1, elevation: 1, shadowRadius: 10, shadowOpacity: .2, backgroundColor: 'white', borderRadius: 30, flexDirection: 'row', overflow: "hidden", justifyContent: 'center', alignContent: 'center', alignItems: 'stretch',
      }}
    >
      {({ hovered }) => (
        <View style={{ flex: 1 }}>
          {/* Animated gradient background */}
          {code && (
            <Animated.View
              style={{
                ...StyleSheet.absoluteFillObject,
                opacity: fadeAnim,
                borderRadius: 10,
              }}
            >
              <LinearGradient
                colors={['#E4D337', '#FF5733']}
                style={{ flex: 1, borderRadius: 10 }}
              />
            </Animated.View>
          )}

          {/* Foreground content */}
          <View
            style={{
              paddingHorizontal: '10%',
              flex: 1,
              flexDirection: 'row',
              backgroundColor: hovered ? 'rgba(90,192,234,0.5)' : 'rgba(90,192,234,0)',
              borderRadius: 10,
            }}
          >
            <View style={[styles.centerEverything, { flex: 1, maxWidth: 40 }]}>
              <GoogleIcon />
            </View>
            <View
              style={{
                flex: 4,
                justifyContent: 'center',
                alignItems: 'center',
                minWidth: 220,
              }}
            >
              <Text
                adjustsFontSizeToFit
                allowFontScaling={false}
                style={{ fontSize: 15, color: 'black', fontWeight: '600' }}
              >
                Sign in with Google
              </Text>
            </View>
          </View>
        </View>
      )}
    </Pressable>
  );
}

export default GoogleButton;